import React from 'react';
import { Lock, EyeOff } from 'lucide-react';
import { useReports } from '../ReportsProvider';
import { REPORT_TABS, REPORT_PRESETS, LOCKED_SECTIONS } from '../utils/reportConstants';

export const SectionIncludeSwitch = ({ tabId }) => {
  const { isSectionIncluded, toggleSectionIncluded } = useReports();
  if (LOCKED_SECTIONS.includes(tabId)) {
    return (
      <span title="Always included" data-testid={`section-include-locked-${tabId}`} className="flex items-center text-gray-400 px-1">
        <Lock className="w-3.5 h-3.5" />
      </span>
    );
  }
  const included = isSectionIncluded(tabId);
  return (
    <button
      type="button"
      role="switch"
      aria-checked={included}
      title={included ? 'Included in report — click to exclude' : 'Excluded from report — click to include'}
      data-testid={`section-include-switch-${tabId}`}
      onClick={(e) => { e.stopPropagation(); toggleSectionIncluded(tabId); }}
      onKeyDown={(e) => e.stopPropagation()}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${included ? 'bg-emerald-500' : 'bg-slate-600'}`}
    >
      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${included ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );
};

export const SectionPresetBar = () => {
  const { activeSectionPreset, applySectionPreset, includedSectionCount } = useReports();
  const presets = [...REPORT_PRESETS, { id: 'custom', name: 'Custom' }];
  return (
    <div className="mb-4 space-y-2" data-testid="section-preset-bar">
      <div className="flex gap-1.5">
        {presets.map(p => (
          <button
            key={p.id}
            type="button"
            disabled={p.id === 'custom'}
            onClick={() => applySectionPreset(p.id)}
            data-testid={`section-preset-${p.id}`}
            className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium border transition-colors duration-200 ${
              activeSectionPreset === p.id
                ? 'bg-purple-600 border-purple-500 text-white'
                : 'bg-slate-700/50 border-slate-600 text-gray-300 hover:bg-slate-700'
            } ${p.id === 'custom' ? 'cursor-default' : ''}`}
          >
            {p.name}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-400 px-1" data-testid="section-included-count">
        {includedSectionCount} of {REPORT_TABS.length} sections included
      </p>
    </div>
  );
};

export const ExcludedSectionBanner = () => {
  const { activeTab, isSectionIncluded, toggleSectionIncluded } = useReports();
  if (!activeTab || isSectionIncluded(activeTab)) return null;
  return (
    <div data-testid="excluded-section-banner" className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3">
      <div className="flex items-center gap-2 text-sm text-amber-300">
        <EyeOff className="w-4 h-4 flex-shrink-0" />
        <span>This section is excluded from the final report. Your content is kept.</span>
      </div>
      <button
        type="button"
        onClick={() => toggleSectionIncluded(activeTab)}
        data-testid="excluded-section-include-btn"
        className="px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-500 text-slate-900 hover:bg-amber-400 transition-colors duration-200"
      >
        Include it
      </button>
    </div>
  );
};
